"use client";
import { useMemo, useState } from "react";
import { DollarSign, Flame } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useRooms } from "@/components/calculators/room-context";
import { toNum, fmt } from "@/components/calculators/calc-constants";
import { NumField, ResultPanel, BigStat, EmailButton } from "@/components/calculators/calc-ui";

/* Loss estimator constants */
const BASE_RATE_BY_CATEGORY: Record<string, number> = { "1": 3.5, "2": 5.5, "3": 9.0 };
const CLASS_MULT: Record<string, number> = { "1": 1.0, "2": 1.2, "3": 1.5, "4": 2.0 };

/* Heating-load envelope multipliers (Manual J lite).
 * Pure air-mass sensible heat is only ~5% of a real heating load — the rest
 * is wall/roof/window conduction + infiltration + duct losses + latent. We
 * fold those into a single "envelope multiplier" tuned to give Manual-J-
 * comparable rough sizing per ASHRAE residential heating-load guidance:
 *   poor   = ~25 BTU/sqft for 8' ceiling
 *   avg    = ~18 BTU/sqft
 *   good   = ~12 BTU/sqft
 * Math: Q = V × 0.018 × envelope_mult × ΔT
 *   where 0.018 = 0.075 lb/ft³ (ρ_air) × 0.24 BTU/lb·°F (c_p)
 */
const ENVELOPE_MULTIPLIERS: Record<string, number> = {
  poor:    20,
  average: 15,
  good:    10,
};

const statStyle: React.CSSProperties = { color: "#ffffff", fontWeight: 600, fontVariantNumeric: "tabular-nums" };

export function ReconstructionTab() {
  const { summary, hasRooms } = useRooms();

  /* Loss estimator state */
  const [lossArea, setLossArea] = useState("");
  const [category, setCategory] = useState("1");
  const [classOfLoss, setClassOfLoss] = useState("2");

  /* Heating BTU state */
  const [btLength, setBtLength] = useState("");
  const [btWidth, setBtWidth] = useState("");
  const [btHeight, setBtHeight] = useState("8");
  const [currentF, setCurrentF] = useState("");
  const [desiredF, setDesiredF] = useState("");
  const [insulation, setInsulation] = useState<"poor" | "average" | "good">("average");

  const cFromF = (v: string) => v ? fmt(((toNum(v) - 32) * 5) / 9, 1) : "";
  const setFromC = (setter: (s: string) => void) => (c: string) => {
    if (!c) return setter("");
    setter(fmt((toNum(c) * 9) / 5 + 32, 1));
  };

  /* Scope summary from rooms */
  const scopeSummary = useMemo(() => {
    if (!hasRooms) return null;
    const area = summary.totalFloorSqFt;
    const volume = summary.totalVolumeFt3;
    const rate = BASE_RATE_BY_CATEGORY["2"];
    const mult = CLASS_MULT["2"];
    const mid = area * rate * mult;
    return {
      area,
      volume,
      roughLow: Math.round(mid * 0.8),
      roughHigh: Math.round(mid * 1.25),
    };
  }, [summary, hasRooms]);

  /* Loss estimate */
  const lossResult = useMemo(() => {
    const A = toNum(lossArea) || (hasRooms ? summary.totalFloorSqFt : 0);
    const rate = BASE_RATE_BY_CATEGORY[category] ?? 3.5;
    const mult = CLASS_MULT[classOfLoss] ?? 1.2;
    const mid = A * rate * mult;
    return { A, rate, mult, mid, low: Math.round(mid * 0.8), high: Math.round(mid * 1.25) };
  }, [lossArea, category, classOfLoss, hasRooms, summary]);

  /* Heating/cooling BTU — Manual-J-lite envelope model.
   *   Q (BTU/hr) = V (ft³) × 0.018 × envelope_multiplier × ΔT (°F)
   * Where 0.018 = ρ_air × c_p × min/hr correction factor that produces
   * Manual-J-comparable rough sizing when paired with the envelope
   * multipliers (poor=20, avg=15, good=10).
   * Previously this formula used 0.24 directly (omitting air-density),
   * which accidentally landed in the right ballpark but was
   * dimensionally wrong. */
  const btuResult = useMemo(() => {
    const L = toNum(btLength), W = toNum(btWidth), H = toNum(btHeight);
    const volume = L * W * H;
    const dT = Math.abs(toNum(desiredF) - toNum(currentF));
    const envMult = ENVELOPE_MULTIPLIERS[insulation];
    const btuPerHour = volume * 0.018 * envMult * dT;
    const tons = btuPerHour / 12000;
    const watts = btuPerHour / 3.412;
    return { volume, dT, btuPerHour, tons, watts, envMult };
  }, [btLength, btWidth, btHeight, currentF, desiredF, insulation]);

  const fmtUsd = (n: number) => n ? `$${n.toLocaleString("en-US")}` : "—";
  const panelStyle = { background: "rgba(10,20,36,0.96)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 14, padding: "20px 22px", marginBottom: 24, boxShadow: "0 4px 24px rgba(0,0,0,0.55)" };
  const sectionTitle = { fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" as const, color: "#D4A84B", marginBottom: 14 };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>

      {/* Scope Summary from rooms */}
      {hasRooms && scopeSummary && (
        <div style={panelStyle}>
          <p style={sectionTitle}>Scope Summary</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
            <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 10, padding: "12px 16px" }}>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Total Affected Area</p>
              <p style={{ fontSize: 24, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums" }}>{fmt(scopeSummary.area)} ft²</p>
            </div>
            <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 10, padding: "12px 16px" }}>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Total Volume</p>
              <p style={{ fontSize: 24, fontWeight: 700, color: "#ffffff", fontVariantNumeric: "tabular-nums" }}>{fmt(scopeSummary.volume)} ft³</p>
            </div>
            <div style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(212,168,75,0.2)", borderRadius: 10, padding: "12px 16px" }}>
              <p style={{ fontSize: 10, color: "rgba(255,255,255,0.55)", textTransform: "uppercase" }}>Rough Range (Cat 2 / Class 2)</p>
              <p style={{ fontSize: 18, fontWeight: 700, color: "#D4A84B", fontVariantNumeric: "tabular-nums" }}>{fmtUsd(scopeSummary.roughLow)} – {fmtUsd(scopeSummary.roughHigh)}</p>
            </div>
          </div>
        </div>
      )}

      {/* Loss Estimator */}
      <div style={panelStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <DollarSign size={16} style={{ color: "#D4A84B" }} />
          <p style={{ ...sectionTitle, marginBottom: 0 }}>Loss / Claim Cost Range Estimator</p>
        </div>
        <div className="grid gap-8 md:grid-cols-2">
          <div className="space-y-4">
            <NumField id="re-area" label={hasRooms ? "Affected floor area (ft²) — leave blank to use rooms" : "Affected floor area (ft²)"} value={lossArea} onChange={setLossArea} placeholder={hasRooms ? fmt(summary.totalFloorSqFt) : "e.g. 1200"} />
            <div className="space-y-1.5">
              <Label htmlFor="re-cat" style={{ color: "rgba(255,255,255,0.85)" }}>Water Category (S500)</Label>
              <Select value={category} onValueChange={(v) => setCategory(v ?? "")}>
                <SelectTrigger id="re-cat" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Cat 1 — clean water</SelectItem>
                  <SelectItem value="2">Cat 2 — gray water</SelectItem>
                  <SelectItem value="3">Cat 3 — black water</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="re-class" style={{ color: "rgba(255,255,255,0.85)" }}>Class of Loss</Label>
              <Select value={classOfLoss} onValueChange={(v) => setClassOfLoss(v ?? "")}>
                <SelectTrigger id="re-class" className="h-10"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Class 1</SelectItem>
                  <SelectItem value="2">Class 2</SelectItem>
                  <SelectItem value="3">Class 3</SelectItem>
                  <SelectItem value="4">Class 4 — specialty</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <ResultPanel
            title="Ballpark Range"
            stat={lossResult.low && lossResult.high ? `${fmtUsd(lossResult.low)} – ${fmtUsd(lossResult.high)}` : "—"}
            unit=""
            rows={[
              { label: "Area", value: lossResult.A ? `${fmt(lossResult.A)} ft²` : "—" },
              { label: "Base rate (per ft²)", value: `$${lossResult.rate.toFixed(2)}` },
              { label: "Class multiplier", value: `${lossResult.mult.toFixed(2)}×` },
              { label: "Midpoint", value: fmtUsd(Math.round((lossResult.low + lossResult.high) / 2)) },
            ]}
            footnote="NOT A QUOTE. Order-of-magnitude range to frame a conversation. Actual scope requires on-site inspection, moisture mapping, and contents handling. Reconstruction, lead remediation, contents pack-out, and structural repairs are additive."
          />
        </div>
        <EmailButton summary={{
          calcLabel: "Loss / Claim Cost Range",
          rows: [
            { label: "Category", value: `Cat ${category}` },
            { label: "Class", value: `Class ${classOfLoss}` },
            { label: "Area", value: `${fmt(lossResult.A)} ft²` },
            { label: "Range", value: `${fmtUsd(lossResult.low)} – ${fmtUsd(lossResult.high)}` },
          ],
        }} />
      </div>

      {/* Heating BTU */}
      <div style={panelStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
          <Flame size={16} style={{ color: "#D4A84B" }} />
          <p style={{ ...sectionTitle, marginBottom: 0 }}>Heating / Cooling BTU</p>
        </div>
        <div className="grid gap-8 lg:grid-cols-2">
          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-3 gap-3">
              <NumField id="bt-l" label="Length (ft)" value={btLength} onChange={setBtLength} placeholder="24" />
              <NumField id="bt-w" label="Width (ft)" value={btWidth} onChange={setBtWidth} placeholder="18" />
              <NumField id="bt-h" label="Height (ft)" value={btHeight} onChange={setBtHeight} placeholder="8" />
            </div>
            <div>
              <Label style={{ color: "rgba(255,255,255,0.85)", marginBottom: 6, display: "block" }}>Insulation quality</Label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 6 }}>
                {(["poor", "average", "good"] as const).map((k) => (
                  <button key={k} type="button" onClick={() => setInsulation(k)} aria-pressed={insulation === k}
                    style={{ padding: "10px 8px", borderRadius: 8, border: "1px solid " + (insulation === k ? "#D4A84B" : "rgba(255,255,255,0.18)"), background: insulation === k ? "#B88A2F" : "transparent", color: insulation === k ? "#080F1A" : "rgba(255,255,255,0.85)", fontWeight: 700, fontSize: 13, cursor: "pointer", textTransform: "capitalize" }}>
                    {k}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <NumField id="bt-cur-f" label="Current (°F)" value={currentF} onChange={setCurrentF} placeholder="78" />
              <NumField id="bt-cur-c" label="Current (°C)" value={cFromF(currentF)} onChange={setFromC(setCurrentF)} placeholder="25" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <NumField id="bt-des-f" label="Desired (°F)" value={desiredF} onChange={setDesiredF} placeholder="72" />
              <NumField id="bt-des-c" label="Desired (°C)" value={cFromF(desiredF)} onChange={setFromC(setDesiredF)} placeholder="22" />
            </div>
          </div>

          <div style={{ background: "rgba(255,255,255,0.06)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)", border: "1px solid rgba(255,255,255,0.22)", borderRadius: 14, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.4)" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <BigStat label="BTU / hr" stat={btuResult.btuPerHour ? fmt(btuResult.btuPerHour) : "—"} highlight />
              <BigStat label="Tons HVAC" stat={btuResult.tons ? fmt(btuResult.tons, 2) : "—"} />
            </div>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <BigStat label="Watts" stat={btuResult.watts ? fmt(btuResult.watts) : "—"} />
              <BigStat label="ΔT" stat={btuResult.dT ? `${fmt(btuResult.dT, 1)} °F` : "—"} />
            </div>
            <dl style={{ padding: "14px 20px", display: "grid", gridTemplateColumns: "1fr auto", rowGap: 8, columnGap: 12, fontSize: 13 }}>
              <dt style={{ color: "rgba(255,255,255,0.7)" }}>Volume</dt>
              <dd style={statStyle}>{btuResult.volume ? `${fmt(btuResult.volume)} ft³` : "—"}</dd>
              <dt style={{ color: "rgba(255,255,255,0.7)" }}>Envelope multiplier</dt>
              <dd style={statStyle}>{ENVELOPE_MULTIPLIERS[insulation].toFixed(0)}×</dd>
            </dl>
            <p style={{ padding: "10px 20px 16px", color: "rgba(255,255,255,0.70)", fontSize: 11, lineHeight: 1.55, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
              Rough estimate only. Does not account for solar gain, envelope leakage, occupancy, duct losses, or latent load.
            </p>
            <div style={{ padding: "0 20px 20px" }}>
              <EmailButton summary={{
                calcLabel: "Heating / Cooling BTU",
                rows: [
                  { label: "Room", value: `${toNum(btLength)} × ${toNum(btWidth)} × ${toNum(btHeight)} ft` },
                  { label: "Volume", value: `${fmt(btuResult.volume)} ft³` },
                  { label: "Current → Desired", value: `${toNum(currentF)} → ${toNum(desiredF)} °F (ΔT ${fmt(btuResult.dT, 1)})` },
                  { label: "Insulation", value: insulation },
                  { label: "BTU / hr", value: fmt(btuResult.btuPerHour) },
                  { label: "Tons HVAC", value: fmt(btuResult.tons, 2) },
                  { label: "Watts", value: fmt(btuResult.watts) },
                ],
              }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
